import React, { useContext, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowForward, Refresh } from '@mui/icons-material';
import { PatientTabsContext } from '../../../lib/contexts';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import PatientNotesRow from './PatientNotesRow';
import PatientAddNotesBox from './PatientAddNotesBox';

const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    marginTop: 16,
  },
  topBar: {
    marginBottom: 16,
  },
  icon: theme.globalClasses.adminTableIcon,
  tableHead: theme.globalClasses.adminTableHead,
  tableRow: theme.globalClasses.adminTableRow,
}));

const PatientNotesBox = ({ summary = false, setSetSubtab }) => {
  const classes = useStyles();
  const theme = useTheme();

  const {
    state: { patientNotes, isLoadingNotes, isRefetchingNotes },
    actions: { refetchNotes },
  } = useContext(PatientTabsContext);

  const matchSM = useMediaQuery(theme.breakpoints.up('sm'));
  const matchMD = useMediaQuery(theme.breakpoints.up('md'));
  const matchLG = useMediaQuery(theme.breakpoints.up('lg'));
  const matchXL = useMediaQuery(theme.breakpoints.up('xl'));

  const topBarMargin = summary ? '12px' : '16px';

  return (
    <Paper className={classes.patientChartCard}>
      <Grid
        container
        spacing={3}
        className={classes.topBar}
      >
        <Grid
          item
          xs={12}
          className="patient-notes-box"
          id="patient-notes-box"
        >
          <Grid
            container
            item
            xs={12}
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: '100%',
              maxWidth: '100%',
              mx: 0,
              padding: 0,
              marginBottom: topBarMargin,
            }}
          >
            {summary ? (
              <>
                <Typography
                  variant={'overline'}
                  sx={{ paddingBottom: 0 }}
                >
                  Visit Notes
                </Typography>
                <Typography
                  variant={'tableHeadLink'}
                  onClick={() => setSetSubtab('6')}
                  sx={{ display: 'flex', alignItems: 'center', paddingBottom: 0 }}
                >
                  View All Notes
                  <IconButton sx={{ paddingLeft: '8px' }}>
                    <ArrowForward className={classes.icon} />
                  </IconButton>
                </Typography>
              </>
            ) : (
              <>
                <Typography variant={'h2'}>Notes and History</Typography>
                <Typography
                  variant={'tableHeadLink'}
                  onClick={refetchNotes}
                  sx={{ display: 'flex', alignItems: 'center', paddingBottom: 0 }}
                >
                  <IconButton sx={{ paddingRight: '8px' }}>
                    <Refresh className={classes.icon} />
                  </IconButton>
                  Refresh
                </Typography>
              </>
            )}
          </Grid>

          {summary ? (
            <PatientAddNotesBox
              summary={true}
              setSetSubtab={setSetSubtab}
            />
          ) : isLoadingNotes ? (
            <AdminProgressBar />
          ) : (
            <>
              <PatientAddNotesBox summary={true} />

              <Box
                className={`${classes.container} ${classes.graphContainer}`}
                sx={{ marginTop: '16px' }}
              >
                <Table>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell align="left">Date</TableCell>
                      <TableCell align="left">Author</TableCell>
                      <TableCell align="left">Note</TableCell>
                      <TableCell align="left">Assessment</TableCell>
                      <TableCell align="left">Consultation</TableCell>
                      <TableCell align="left">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Boolean(patientNotes && patientNotes.length) &&
                      patientNotes
                        .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
                        .slice(0, summary ? 3 : patientNotes.length)
                        .map((item, index) => {
                          return (
                            <PatientNotesRow
                              key={index}
                              row={item}
                              summary={summary}
                              refetch={refetchNotes}
                            />
                          );
                        })}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PatientNotesBox;
